import type {
  Blueprint,
  GeneratedResponse,
  PendingAction,
  SentinelAiEventLogWireDto,
  SentinelWorkspaceDetailWireDto,
} from '@/modules/sentinel-workspace/types';
import { REFINE_STYLES, RESPONSE_ORIGIN } from '@/modules/sentinel-workspace/types';
import { sentinelErrorActions, sentinelErrorMessage } from '@/modules/sentinel-workspace/error-copy';
import { currentBlueprintFromApi } from './sentinelWorkspace.blueprint.adapter';
import { isAutomationOwned } from './sentinelWorkspace.conversation.adapter';
import { traceStepsFromEvents } from './sentinelWorkspace.trace.adapter';

export type SentinelArtifact =
  | { kind: 'blueprint'; blueprint: Blueprint }
  | { kind: 'hitl'; action: PendingAction }
  | { kind: 'response'; response: GeneratedResponse }
  | {
      kind: 'automated';
      response: GeneratedResponse;
      delivery: AutomationDelivery;
      message: string | null;
    }
  | { kind: 'error'; message: string; retryable: boolean; manual: boolean }
  | { kind: 'working' };

export type AutomationDelivery = 'sending' | 'sent' | 'failed' | 'blocked' | 'unknown';

const AUTOMATION_DELIVERY_BY_EVENT: Readonly<Record<string, AutomationDelivery>> = {
  automation_dispatch_completed: 'sent',
  automation_dispatch_failed: 'failed',
  automation_dispatch_blocked: 'blocked',
  automation_dispatch_unknown: 'unknown',
  automation_dispatch_started: 'sending',
};

const ACTION_PROGRESS_VERBS: Record<string, string> = {
  approve: 'Approving',
  cancel: 'Cancelling',
  close: 'Closing',
  create: 'Creating',
  refund: 'Refunding',
  send: 'Sending',
  update: 'Updating',
};

const RESPONSE_FAILURE_CODES = new Set([
  'DRAFT_REFINEMENT_NOT_APPLIED',
  'HITL_ACTION_OUTCOME_UNKNOWN',
  'HITL_ACTION_COMPLETED_DRAFT_FAILED',
]);

function isResponseFailure(dto: SentinelWorkspaceDetailWireDto): boolean {
  const current = dto.aiEventLogs.find((event) => event.id === dto.ticket.current_ai_event_log_id);
  return Boolean(current?.response_message) || dto.ticket.response_retries_used > 0 || RESPONSE_FAILURE_CODES.has(dto.ticket.last_error_code ?? '');
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function stringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export interface ResponseHistory {
  versions: GeneratedResponse[];
  refineLabels: Record<string, string>;
}

function sentinelEvents(event: SentinelAiEventLogWireDto): Record<string, unknown>[] {
  const events = event.extra_details?.['sentinelEvents'];
  return Array.isArray(events)
    ? events.filter((item): item is Record<string, unknown> => record(item) !== null)
    : [];
}

/** The AEL marker is the source of truth that legacy automation owns this turn. */
function automationEventLogFromApi(dto: SentinelWorkspaceDetailWireDto): SentinelAiEventLogWireDto | null {
  const currentTurn = dto.aiEventLogs.filter((event) => event.root_trace_id === dto.ticket.root_trace_id);
  const { active_generation_trace_id: activeTraceId, active_generation_request_id: activeRequestId } = dto.ticket;
  const activeTurn =
    activeTraceId && activeRequestId
      ? currentTurn.filter((event) => event.trace_id === activeTraceId && event.request_id === activeRequestId)
      : currentTurn;
  const current = activeTurn.find((event) => event.id === dto.ticket.current_ai_event_log_id);
  const newestFirst = [...activeTurn].reverse();
  const candidates = current
    ? [current, ...newestFirst.filter((event) => event.id !== current.id)]
    : newestFirst;
  return candidates.find((event) => sentinelEvents(event).some((item) => item['type'] === 'automation_selected')) ?? null;
}

function refinementLabel(payload: Record<string, unknown>): string | null {
  const prompt = stringValue(payload['prompt']).trim();
  if (prompt) return prompt;

  const command = stringValue(payload['command']).trim();
  if (!command) return null;
  const preset = REFINE_STYLES.find((style) => style.value === command);
  return `Refine : ${(preset?.label ?? command).toLowerCase()}`;
}

/** Rebuilds the response/refinement conversation from the ticket's persisted AI event logs. */
export function responseHistoryFromApi(dto: SentinelWorkspaceDetailWireDto): ResponseHistory {
  const currentTurn = dto.aiEventLogs
    .filter((event) => event.root_trace_id === dto.ticket.root_trace_id)
    .sort((left, right) => left.created_at.localeCompare(right.created_at) || left.id - right.id);
  const refinements: { position: number; requestId: string; label: string; claimed: boolean }[] = [];

  currentTurn.forEach((event, position) => {
    for (const storedEvent of sentinelEvents(event)) {
      if (storedEvent['type'] !== 'response_refinement_requested') continue;
      const payload = record(storedEvent['payload']);
      if (!payload) continue;
      const requestId = stringValue(payload['requestId']).trim();
      const label = refinementLabel(payload);
      if (requestId && label) refinements.push({ position, requestId, label, claimed: false });
    }
  });

  let lastConfidence: number | null = null;
  const refineLabels: Record<string, string> = {};
  const versions = currentTurn.flatMap((event, position) => {
    if (event.confidence_score != null) lastConfidence = event.confidence_score;
    if (!event.response_message) return [];
    // A replan answers the refine under a new request id, so an earlier unclaimed ask still names it.
    const refinement =
      refinements.find((item) => !item.claimed && item.requestId === event.request_id) ??
      refinements.find((item) => !item.claimed && item.position < position);
    if (refinement) refinement.claimed = true;
    const refinementLabelForEvent = refinement?.label;
    const isRefinement =
      event.extra_details?.['sentinelAction'] === 'response_refinement' || refinementLabelForEvent !== undefined;
    const response: GeneratedResponse = {
      id: String(event.id),
      version: 0,
      markdown: event.response_message,
      confidencePct: lastConfidence == null ? null : Math.round(lastConfidence * 100),
      knowledgeBaseIds: event.knowledge_base_list ?? [],
      ruleIds: event.rule_list ?? [],
      trace: traceStepsFromEvents(event.extra_details?.['sentinelEvents']),
      origin: isRefinement ? RESPONSE_ORIGIN.refine : RESPONSE_ORIGIN.generate,
    };
    if (refinementLabelForEvent) refineLabels[response.id] = refinementLabelForEvent;
    return [response];
  });

  const numberedVersions = versions.map((response, index) => ({ ...response, version: index + 1 }));
  // Older records may only expose the current draft, without its request metadata.
  if (numberedVersions.length === 1 && dto.ticket.response_retries_used > 0) {
    numberedVersions[0] = {
      ...numberedVersions[0]!,
      version: dto.ticket.response_retries_used + 1,
      origin: RESPONSE_ORIGIN.refine,
    };
  }

  return {
    versions: numberedVersions,
    refineLabels,
  };
}

function progressLabel(label: string): string {
  const words = label.trim().split(/\s+/);
  const progressVerb = ACTION_PROGRESS_VERBS[words[0]?.toLowerCase()];
  if (!progressVerb) return `${label}...`;
  const rest = words.slice(1).join(' ');
  return `${rest ? `${progressVerb} ${rest.toLowerCase()}` : progressVerb.toLowerCase()}...`;
}

/** One approval request, as Brahma worded it, in the shape the action card draws. */
export function pendingActionFromRequest(request: Record<string, unknown>): PendingAction {
  const details = record(request['details']);
  const actionLabel = stringValue(details?.['action'], 'Approve action');
  return {
    id: String(request['approval_id'] ?? ''),
    label: stringValue(details?.['label'], String(request['tool_name'] ?? 'Action requires approval')),
    description: stringValue(request['reason'], 'Review this action before continuing.'),
    ctaLabel: actionLabel,
    progressLabel: progressLabel(actionLabel),
    confidencePct: 0,
    sources: [],
  };
}

function hitlArtifact(dto: SentinelWorkspaceDetailWireDto): SentinelArtifact {
  const event = [...dto.aiEventLogs].reverse().find((item) => {
    const requests = item.approval_requests?.['requests'];
    return Array.isArray(requests) && requests.length > 0;
  });
  const requests = event?.approval_requests?.['requests'];
  const request = Array.isArray(requests) ? (requests[0] as Record<string, unknown> | undefined) : undefined;
  if (!request) return { kind: 'working' };
  return { kind: 'hitl', action: pendingActionFromRequest(request) };
}

function responseArtifact(dto: SentinelWorkspaceDetailWireDto): SentinelArtifact {
  const history = responseHistoryFromApi(dto);
  const current = history.versions.find((response) => response.id === String(dto.ticket.current_ai_event_log_id));
  if (!current) {
    const pending = dto.aiEventLogs.find((event) => String(event.id) === String(dto.ticket.current_ai_event_log_id));
    // The ticket points at a request with no text yet, so the next draft is still on its way.
    if (pending?.event_kind === 'request' && !pending.response_message) return { kind: 'working' };
  }
  const response = current ?? history.versions.at(-1);
  return response ? { kind: 'response', response } : { kind: 'working' };
}

function automatedArtifact(dto: SentinelWorkspaceDetailWireDto): SentinelArtifact | null {
  const event = automationEventLogFromApi(dto);
  if (!event || !isAutomationOwned(event)) return null;

  const response = responseHistoryFromApi(dto).versions.find((item) => item.id === String(event.id));
  if (!response) return { kind: 'working' };

  const deliveryEvent = [...sentinelEvents(event)]
    .reverse()
    .find((item) => Object.hasOwn(AUTOMATION_DELIVERY_BY_EVENT, stringValue(item['type'])));
  const eventType = stringValue(deliveryEvent?.['type']);
  const delivery = AUTOMATION_DELIVERY_BY_EVENT[eventType] ?? 'sending';
  const payload = record(deliveryEvent?.['payload']);
  const detail = stringValue(payload?.['detail']).trim() || dto.ticket.last_error_detail;
  return { kind: 'automated', response, delivery, message: detail || null };
}

export function artifactFromApi(dto: SentinelWorkspaceDetailWireDto): SentinelArtifact {
  const automated = automatedArtifact(dto);
  if (automated) return automated;

  // Show errors for older stuck tickets without changing their saved status.
  if (dto.ticket.status === 'ingesting' &&
      ['SENTINEL_ASSIGNMENT_FAILED', 'SENTINEL_CLAIM_FAILED'].includes(dto.ticket.last_error_code ?? '')) {
    return {
      kind: 'error',
      message: sentinelErrorMessage(dto.ticket.last_error_code, 'blueprint'),
      retryable: true,
      manual: true,
    };
  }

  switch (dto.ticket.status) {
    case 'blueprint_generated': {
      const blueprint = currentBlueprintFromApi(dto);
      return blueprint ? { kind: 'blueprint', blueprint } : { kind: 'working' };
    }
    case 'awaiting_hitl':
      return hitlArtifact(dto);
    case 'response_generated':
      return responseArtifact(dto);
    case 'failed': {
      const stage = isResponseFailure(dto) ? 'response' : 'blueprint';
      const actions = sentinelErrorActions(dto.ticket.last_error_code);
      return {
        kind: 'error',
        message: sentinelErrorMessage(dto.ticket.last_error_code, stage),
        retryable: actions.retry,
        manual: actions.manual,
      };
    }
    // Do not regenerate when delivery is uncertain.
    case 'dispatch_unknown':
      return {
        kind: 'error',
        message: sentinelErrorMessage(dto.ticket.last_error_code, 'dispatch'),
        retryable: false,
        manual: false,
      };
    default:
      return { kind: 'working' };
  }
}

/** Names the refine still running on the server, so reopening the ticket can show it again. */
export function activeRefineLabelFromApi(dto: SentinelWorkspaceDetailWireDto): string | null {
  const requestId = dto.ticket.active_generation_request_id;
  if (!requestId) return null;

  for (const event of [...dto.aiEventLogs].reverse()) {
    for (const stored of [...sentinelEvents(event)].reverse()) {
      if (stored['type'] !== 'response_refinement_requested') continue;
      const payload = record(stored['payload']);
      if (stringValue(payload?.['requestId']).trim() !== requestId) continue;
      return payload ? refinementLabel(payload) : null;
    }
  }
  return null;
}
